import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Save, X, Loader2, RefreshCw, Edit2, Power, PowerOff, AlertTriangle } from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

/**
 * Each category in the Facts Library has a specific set of fields.
 * Schema mirrors what hg_voice._format_facts expects.
 */
const CATEGORIES = [
  {
    id: 'fitters',
    label: 'Fitters & Coaches',
    summary: 'HG-certified fitters and teaching pros the AI is allowed to cite by name.',
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'title', label: 'Title', placeholder: 'H-G Master Golf Clubfitter' },
      { name: 'facility', label: 'Facility / Academy' },
      { name: 'location', label: 'Location', placeholder: 'City, State / Country' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    primaryField: 'name',
    secondaryField: 'facility',
    tertiaryField: 'location',
  },
  {
    id: 'fitting_moments',
    label: 'Fitting Moments',
    summary: 'Real, verifiable fitting stories. Each one becomes available to social posts and newsletters.',
    fields: [
      { name: 'summary', label: 'Summary', type: 'textarea', required: true, placeholder: 'A 19-degree driver and a ball-position change took a beginner from 85 → 135 yards in five minutes...' },
      { name: 'fitter', label: 'Fitter (attribution)', placeholder: 'Bob Duncan, H-G Master Golf Clubfitter (Oregon)' },
      { name: 'player_desc', label: 'Player Description' },
      { name: 'result', label: 'Result' },
      { name: 'source', label: 'Source / Reference' },
    ],
    primaryField: 'summary',
    secondaryField: 'fitter',
  },
  {
    id: 'testimonials',
    label: 'Testimonials',
    summary: 'Approved customer/coach quotes. Quotes WITHOUT permission_granted are never used by the AI.',
    fields: [
      { name: 'quote', label: 'Quote', type: 'textarea', required: true },
      { name: 'attribution', label: 'Attribution', required: true, placeholder: 'Player name, City' },
      { name: 'permission_granted', label: 'Permission granted to publish?', type: 'checkbox' },
      { name: 'context', label: 'Context' },
    ],
    primaryField: 'quote',
    secondaryField: 'attribution',
  },
  {
    id: 'products',
    label: 'Products',
    summary: 'Approved product names + a short, accurate description.',
    fields: [
      { name: 'name', label: 'Name', required: true, placeholder: 'Fly Series' },
      { name: 'category', label: 'Category', placeholder: 'drivers / irons / fitting service' },
      { name: 'details', label: 'Details', type: 'textarea' },
    ],
    primaryField: 'name',
    secondaryField: 'category',
  },
  {
    id: 'stats',
    label: 'Stats & Numbers',
    summary: 'Approved numbers the AI can cite. Anything not in this list, AI writes generically.',
    fields: [
      { name: 'label', label: 'Label', required: true, placeholder: 'Year founded' },
      { name: 'value', label: 'Value', required: true, placeholder: '1982' },
      { name: 'context', label: 'Context (optional)' },
    ],
    primaryField: 'label',
    secondaryField: 'value',
  },
];


function FieldInput({ field, value, onChange }) {
  if (field.type === 'textarea') {
    return (
      <textarea
        data-testid={`facts-field-${field.name}`}
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder || ''}
        rows={3}
        className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
      />
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          data-testid={`facts-field-${field.name}`}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(field.name, e.target.checked)}
          className="accent-hg-green"
        />
        <span className="text-sm text-hg-text font-sans">{field.label}</span>
      </label>
    );
  }
  return (
    <input
      data-testid={`facts-field-${field.name}`}
      type="text"
      value={value || ''}
      onChange={(e) => onChange(field.name, e.target.value)}
      placeholder={field.placeholder || ''}
      className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
    />
  );
}


function FactEditor({ category, existing, onSave, onCancel, saving }) {
  const [data, setData] = useState(existing?.data || {});
  const handleField = (k, v) => setData({ ...data, [k]: v });

  const canSave = category.fields
    .filter((f) => f.required)
    .every((f) => (data[f.name] || '').toString().trim().length > 0);

  return (
    <div data-testid="fact-editor" className="bg-hg-bg border border-hg-green/30 rounded-sm p-4 space-y-3">
      {category.fields.map((f) => (
        f.type === 'checkbox' ? (
          <div key={f.name}>
            <FieldInput field={f} value={data[f.name]} onChange={handleField} />
          </div>
        ) : (
          <div key={f.name}>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">
              {f.label}{f.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <FieldInput field={f} value={data[f.name]} onChange={handleField} />
          </div>
        )
      ))}
      <div className="flex gap-2 pt-1">
        <button
          data-testid="fact-editor-save"
          onClick={() => onSave(data)}
          disabled={!canSave || saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {existing ? 'Save changes' : 'Add fact'}
        </button>
        <button
          data-testid="fact-editor-cancel"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text"
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}


function FactRow({ fact, category, onEdit, onToggle, onDelete, busy }) {
  const d = fact.data || {};
  const primary = d[category.primaryField] || '(untitled)';
  const secondary = category.secondaryField ? d[category.secondaryField] : '';
  const tertiary = category.tertiaryField ? d[category.tertiaryField] : '';
  const needsPermission = category.id === 'testimonials' && !d.permission_granted;

  return (
    <div
      data-testid={`fact-row-${fact.id}`}
      className={`bg-hg-bg border rounded-sm p-3 flex items-start justify-between gap-3 ${fact.active ? 'border-hg-border' : 'border-hg-border/40 opacity-60'}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-sans text-hg-text truncate">{primary}</div>
        {(secondary || tertiary) && (
          <div className="text-[11px] text-hg-text-dim font-sans truncate">
            {[secondary, tertiary].filter(Boolean).join(' · ')}
          </div>
        )}
        {needsPermission && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-sans text-yellow-400">
            <AlertTriangle size={10} /> Permission not granted — AI will not use this quote.
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          data-testid={`fact-edit-${fact.id}`}
          onClick={() => onEdit(fact)}
          className="p-1.5 text-hg-text-dim hover:text-hg-green border border-transparent hover:border-hg-green/30 rounded-sm"
          title="Edit"
        ><Edit2 size={12} strokeWidth={1.5} /></button>
        <button
          data-testid={`fact-toggle-${fact.id}`}
          onClick={() => onToggle(fact)}
          disabled={busy}
          className="p-1.5 text-hg-text-dim hover:text-hg-text border border-transparent hover:border-hg-border rounded-sm"
          title={fact.active ? 'Disable (hide from AI)' : 'Enable (allow AI to cite)'}
        >
          {fact.active ? <Power size={12} strokeWidth={1.5} /> : <PowerOff size={12} strokeWidth={1.5} />}
        </button>
        <button
          data-testid={`fact-delete-${fact.id}`}
          onClick={() => onDelete(fact)}
          disabled={busy}
          className="p-1.5 text-red-400/70 hover:text-red-400 border border-transparent hover:border-red-400/30 rounded-sm"
          title="Delete"
        ><X size={12} strokeWidth={1.5} /></button>
      </div>
    </div>
  );
}


export default function FactsLibrary() {
  const [activeCat, setActiveCat] = useState(CATEGORIES[0].id);
  const [factsByCat, setFactsByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // {fact}
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const out = {};
      for (const c of CATEGORIES) {
        const res = await axios.get(`${API}/facts/list?category=${c.id}&only_active=false`);
        out[c.id] = res.data.facts || [];
      }
      setFactsByCat(out);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load facts');
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const handleSave = async (data) => {
    setBusy(true);
    try {
      if (editing) {
        await axios.patch(`${API}/facts/${editing.id}`, { data });
      } else {
        await axios.post(`${API}/facts/add`, { category: activeCat, data });
      }
      setEditing(null);
      setAdding(false);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    }
    setBusy(false);
  };

  const handleToggle = async (fact) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/facts/${fact.id}`, { active: !fact.active });
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Toggle failed');
    }
    setBusy(false);
  };

  const handleDelete = async (fact) => {
    if (!window.confirm(`Delete this ${activeCat.slice(0, -1)}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/facts/${fact.id}`);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
    setBusy(false);
  };

  const handleSeed = async () => {
    if (!window.confirm('Re-seed facts from the HG archive (T2T newsletters, voice guide, website)? Existing entries will be updated, not duplicated.')) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/facts/seed-from-archive`);
      await reload();
      setError('');
      window.alert(`Seed complete: ${res.data.inserted} new, ${res.data.updated} updated.`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Seed failed');
    }
    setSeeding(false);
  };

  const cat = CATEGORIES.find((c) => c.id === activeCat);
  const list = factsByCat[activeCat] || [];

  return (
    <div className="space-y-3" data-testid="facts-library">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Facts Library</h2>
        <button
          data-testid="facts-seed-btn"
          onClick={handleSeed}
          disabled={seeding}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green disabled:opacity-40"
        >
          {seeding ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} strokeWidth={1.5} />}
          Re-seed from HG archive
        </button>
      </div>
      <p className="text-xs text-hg-text-dim font-sans leading-relaxed">
        These are the ONLY specifics the AI is allowed to cite by name (people, quotes, numbers, anecdotes). If the AI needs a specific and nothing in this library fits, it will write generically rather than invent. Toggle entries off to hide them from the AI without deleting.
      </p>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1 border-b border-hg-border">
        {CATEGORIES.map((c) => {
          const isActive = c.id === activeCat;
          const count = (factsByCat[c.id] || []).length;
          return (
            <button
              key={c.id}
              data-testid={`facts-tab-${c.id}`}
              onClick={() => { setActiveCat(c.id); setAdding(false); setEditing(null); }}
              className={`px-3 py-2 text-xs font-sans border-b-2 transition-colors flex items-center gap-1.5 ${
                isActive ? 'border-hg-green text-hg-text' : 'border-transparent text-hg-text-dim hover:text-hg-text'
              }`}
            >
              {c.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${isActive ? 'bg-hg-green/20 text-hg-green' : 'bg-hg-border/60 text-hg-text-dim'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Category summary + add button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <p className="text-[11px] text-hg-text-dim font-sans">{cat?.summary}</p>
        {!adding && !editing && (
          <button
            data-testid="facts-add-btn"
            onClick={() => { setAdding(true); setEditing(null); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90"
          >
            <Plus size={12} /> Add {cat?.label.slice(0, -1)}
          </button>
        )}
      </div>

      {(adding || editing) && cat && (
        <FactEditor
          category={cat}
          existing={editing}
          onSave={handleSave}
          onCancel={() => { setAdding(false); setEditing(null); }}
          saving={busy}
        />
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={18} className="animate-spin text-hg-green" />
        </div>
      ) : list.length === 0 ? (
        <div className="text-xs text-hg-text-dim font-sans text-center py-8">
          No entries in this category yet. Use "Re-seed from HG archive" to load known-good defaults.
        </div>
      ) : (
        <div className="space-y-1.5">
          {list.map((fact) => (
            <FactRow
              key={fact.id}
              fact={fact}
              category={cat}
              onEdit={(f) => { setEditing(f); setAdding(false); }}
              onToggle={handleToggle}
              onDelete={handleDelete}
              busy={busy}
            />
          ))}
        </div>
      )}

      {error && (
        <div className="text-xs text-red-400 font-sans">{error}</div>
      )}
    </div>
  );
}
